import { useCallback, useEffect, useState } from "react";
import {
  createTransporterFleetDriver,
  patchTransporterFleetDriver,
  patchTransporterFleetVehicle,
  type TransporterFleetDriverDto,
  type TransporterFleetVehicleDto,
} from "../lib/fetch-transporter-delivery-ops";

function fleetPatchErrorMessage(err: unknown): string {
  if (!(err instanceof Error)) return "Update failed.";
  const m = err.message;
  if (m.includes("driver_on_shift")) {
    return "End the driver’s shift before deactivating.";
  }
  if (m.includes("duplicate_med_employee")) {
    return "Another driver already uses this MED employee license number.";
  }
  if (m.includes("fleet_driver_patch_http_409")) {
    return "Conflict — check MED license uniqueness or shift status.";
  }
  if (m.includes("fleet_vehicle_patch_http_")) {
    return m.replace(/^fleet_vehicle_patch_http_\d+/, "Could not save vehicle");
  }
  return m.replace(/^fleet_driver_patch_http_\d+/, "Could not save driver");
}

function fleetCreateErrorMessage(err: unknown): string {
  if (!(err instanceof Error)) return "Could not create driver.";
  const m = err.message;
  if (m.includes("fleet_driver_create_http_404")) {
    return "The API returned “not found” (404). Redeploy the API with the latest code, or if the web app is on a different host than the API, set VITE_API_BASE_URL to the API origin at build time.";
  }
  if (m.includes("fleet_driver_create_http_403")) {
    return "You don’t have permission to add drivers (dispatcher or platform admin only).";
  }
  if (m.includes("clerk_email_already_registered")) {
    return "That email is already registered. Use a different email or contact support.";
  }
  if (m.includes("duplicate_med_employee")) {
    return "Another driver already uses this MED employee license number.";
  }
  if (m.includes("fleet_driver_create_http_502")) {
    return "Could not create the sign-in account. Try again or contact support.";
  }
  return m.replace(/^fleet_driver_create_http_\d+/, "Could not create driver");
}

type DriverDialogProps = {
  open: boolean;
  driver: TransporterFleetDriverDto | null;
  transporterId: string;
  getToken: () => Promise<string | null>;
  onClose: () => void;
  onSaved: () => void;
};

export function FleetDriverEditDialog({
  open,
  driver,
  transporterId,
  getToken,
  onClose,
  onSaved,
}: DriverDialogProps) {
  const [medEmp, setMedEmp] = useState("");
  const [medExp, setMedExp] = useState("");
  const [coDl, setCoDl] = useState("");
  const [coExp, setCoExp] = useState("");
  const [active, setActive] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !driver) return;
    setMedEmp(driver.medEmployeeLicenseNumber);
    setMedExp(driver.medLicenseExpiresAt.slice(0, 10));
    setCoDl(driver.coDriversLicenseNumber);
    setCoExp(driver.coLicenseExpiresAt.slice(0, 10));
    setActive(driver.isActive);
    setErr(null);
  }, [open, driver]);

  const submit = useCallback(async () => {
    if (!driver) return;
    setBusy(true);
    setErr(null);
    try {
      await patchTransporterFleetDriver(getToken, transporterId, driver.driverId, {
        medEmployeeLicenseNumber: medEmp.trim(),
        medLicenseExpiresAt: medExp.trim().slice(0, 10),
        coDriversLicenseNumber: coDl.trim(),
        coLicenseExpiresAt: coExp.trim().slice(0, 10),
        isActive: active,
      });
      onSaved();
      onClose();
    } catch (e) {
      setErr(fleetPatchErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }, [driver, getToken, transporterId, medEmp, medExp, coDl, coExp, active, onSaved, onClose]);

  if (!open || !driver) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
      <div className="td-surface max-h-[90vh] w-full max-w-lg overflow-y-auto p-6">
        <h2 className="font-display text-lg font-semibold text-dark">Edit driver</h2>
        <p className="mt-1 text-sm text-gray">
          {driver.fullName}
          {driver.onShift ? (
            <span className="ml-2 text-amber-700">On shift — deactivate only after shift ends.</span>
          ) : null}
        </p>
        <div className="mt-4 space-y-3 text-sm">
          <label className="block">
            <span className="text-xs text-gray">MED employee license #</span>
            <input
              className="input-field mt-1 font-mono"
              value={medEmp}
              onChange={(e) => setMedEmp(e.target.value)}
            />
          </label>
          <label className="block">
            <span className="text-xs text-gray">MED expires</span>
            <input
              type="date"
              className="input-field mt-1 font-mono"
              value={medExp}
              onChange={(e) => setMedExp(e.target.value)}
            />
          </label>
          <label className="block">
            <span className="text-xs text-gray">Colorado driver license #</span>
            <input
              className="input-field mt-1 font-mono"
              value={coDl}
              onChange={(e) => setCoDl(e.target.value)}
            />
          </label>
          <label className="block">
            <span className="text-xs text-gray">CO DL expires</span>
            <input
              type="date"
              className="input-field mt-1 font-mono"
              value={coExp}
              onChange={(e) => setCoExp(e.target.value)}
            />
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-dark">
            <input
              type="checkbox"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
              className="rounded border-border"
            />
            Active (eligible for assignment)
          </label>
        </div>
        {err ? <p className="mt-3 text-sm text-red">{err}</p> : null}
        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            className="btn-secondary"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary disabled:opacity-50"
            onClick={() => void submit()}
            disabled={busy}
          >
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

type CreateDriverDialogProps = {
  open: boolean;
  transporterId: string;
  getToken: () => Promise<string | null>;
  onClose: () => void;
  onSaved: () => void;
};

export function FleetDriverCreateDialog({
  open,
  transporterId,
  getToken,
  onClose,
  onSaved,
}: CreateDriverDialogProps) {
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [medEmp, setMedEmp] = useState("");
  const [medExp, setMedExp] = useState("");
  const [coDl, setCoDl] = useState("");
  const [coExp, setCoExp] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setEmail("");
    setFullName("");
    setPhone("");
    setMedEmp("");
    setMedExp("");
    setCoDl("");
    setCoExp("");
    setErr(null);
  }, [open]);

  const submit = useCallback(async () => {
    const em = email.trim();
    const fn = fullName.trim();
    if (!em || !fn || !medEmp.trim() || !coDl.trim() || !medExp || !coExp) {
      setErr("Email, name, license numbers, and expiry dates are required.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const ph = phone.trim();
      const created = await createTransporterFleetDriver(getToken, transporterId, {
        email: em,
        fullName: fn,
        phone: ph.length > 0 ? ph : null,
        medEmployeeLicenseNumber: medEmp.trim(),
        medLicenseExpiresAt: medExp.trim().slice(0, 10),
        coDriversLicenseNumber: coDl.trim(),
        coLicenseExpiresAt: coExp.trim().slice(0, 10),
      });
      if (!created.clerkInvitationEmailSent) {
        setErr(
          created.clerkInvitationError != null && created.clerkInvitationError.length > 0
            ? `Driver was created, but Clerk did not send the invitation email: ${created.clerkInvitationError}`
            : "Driver was created, but Clerk did not confirm sending the invitation email. They can try “Forgot password” on the driver app.",
        );
        onSaved();
        return;
      }
      onSaved();
      onClose();
    } catch (e) {
      setErr(fleetCreateErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }, [email, fullName, phone, medEmp, medExp, coDl, coExp, getToken, transporterId, onSaved, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
      <div className="td-surface max-h-[90vh] w-full max-w-lg overflow-y-auto p-6">
        <h2 className="font-display text-lg font-semibold text-dark">Add driver</h2>
        <p className="mt-1 text-sm text-gray">
          Creates the account in Clerk with no password. Clerk emails them an invitation link to finish sign-in (set a
          password or use your instance’s email link / SSO). If no email arrives, check spam and Clerk Dashboard →
          deliverability; the driver can also use “Forgot password” on the driver app with this email.
        </p>
        <div className="mt-4 space-y-3 text-sm">
          <label className="block">
            <span className="text-xs text-gray">Email (sign-in)</span>
            <input
              className="input-field mt-1"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <label className="block">
            <span className="text-xs text-gray">Full name</span>
            <input
              className="input-field mt-1"
              autoComplete="name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
            />
          </label>
          <label className="block">
            <span className="text-xs text-gray">Phone (optional)</span>
            <input
              className="input-field mt-1"
              type="tel"
              autoComplete="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </label>
          <label className="block">
            <span className="text-xs text-gray">MED employee license #</span>
            <input
              className="input-field mt-1 font-mono"
              value={medEmp}
              onChange={(e) => setMedEmp(e.target.value)}
            />
          </label>
          <label className="block">
            <span className="text-xs text-gray">MED expires</span>
            <input
              type="date"
              className="input-field mt-1 font-mono"
              value={medExp}
              onChange={(e) => setMedExp(e.target.value)}
            />
          </label>
          <label className="block">
            <span className="text-xs text-gray">Colorado driver license #</span>
            <input
              className="input-field mt-1 font-mono"
              value={coDl}
              onChange={(e) => setCoDl(e.target.value)}
            />
          </label>
          <label className="block">
            <span className="text-xs text-gray">CO DL expires</span>
            <input
              type="date"
              className="input-field mt-1 font-mono"
              value={coExp}
              onChange={(e) => setCoExp(e.target.value)}
            />
          </label>
        </div>
        {err ? <p className="mt-3 text-sm text-red">{err}</p> : null}
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary disabled:opacity-50"
            onClick={() => void submit()}
            disabled={busy}
          >
            {busy ? "Creating…" : "Create driver"}
          </button>
        </div>
      </div>
    </div>
  );
}

type VehicleDialogProps = {
  open: boolean;
  vehicle: TransporterFleetVehicleDto | null;
  transporterId: string;
  getToken: () => Promise<string | null>;
  onClose: () => void;
  onSaved: () => void;
};

export function FleetVehicleEditDialog({
  open,
  vehicle,
  transporterId,
  getToken,
  onClose,
  onSaved,
}: VehicleDialogProps) {
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [year, setYear] = useState(2024);
  const [color, setColor] = useState("");
  const [plate, setPlate] = useState("");
  const [vin, setVin] = useState("");
  const [regNum, setRegNum] = useState("");
  const [regExp, setRegExp] = useState("");
  const [insPol, setInsPol] = useState("");
  const [insExp, setInsExp] = useState("");
  const [alarm, setAlarm] = useState(false);
  const [locked, setLocked] = useState(false);
  const [video, setVideo] = useState(false);
  const [videoDays, setVideoDays] = useState(40);
  const [gps, setGps] = useState("");
  const [maxLoad, setMaxLoad] = useState(1_000_000);
  const [active, setActive] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !vehicle) return;
    setMake(vehicle.make);
    setModel(vehicle.model);
    setYear(vehicle.year);
    setColor(vehicle.color);
    setPlate(vehicle.licensePlate);
    setVin(vehicle.vin ?? "");
    setRegNum(vehicle.registrationNumber ?? "");
    setRegExp(vehicle.registrationExpiresAt.slice(0, 10));
    setInsPol(vehicle.insurancePolicyNumber);
    setInsExp(vehicle.insuranceExpiresAt.slice(0, 10));
    setAlarm(vehicle.hasSecurityAlarm);
    setLocked(vehicle.hasLockedOpaqueStorage);
    setVideo(vehicle.hasVideoSurveillance);
    setVideoDays(vehicle.videoRetentionDays);
    setGps(vehicle.gpsDeviceId ?? "");
    setMaxLoad(vehicle.maxLoadValueCents);
    setActive(vehicle.isActive);
    setErr(null);
  }, [open, vehicle]);

  const submit = useCallback(async () => {
    if (!vehicle) return;
    setBusy(true);
    setErr(null);
    try {
      await patchTransporterFleetVehicle(getToken, transporterId, vehicle.vehicleId, {
        make: make.trim(),
        model: model.trim(),
        year,
        color: color.trim(),
        licensePlate: plate.trim(),
        vin: vin.trim() || null,
        registrationNumber: regNum.trim() || null,
        registrationExpiresAt: regExp.trim().slice(0, 10),
        insurancePolicyNumber: insPol.trim(),
        insuranceExpiresAt: insExp.trim().slice(0, 10),
        hasSecurityAlarm: alarm,
        hasLockedOpaqueStorage: locked,
        hasVideoSurveillance: video,
        videoRetentionDays: videoDays,
        gpsDeviceId: gps.trim() || null,
        maxLoadValueCents: maxLoad,
        isActive: active,
      });
      onSaved();
      onClose();
    } catch (e) {
      setErr(fleetPatchErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }, [
    vehicle,
    getToken,
    transporterId,
    make,
    model,
    year,
    color,
    plate,
    vin,
    regNum,
    regExp,
    insPol,
    insExp,
    alarm,
    locked,
    video,
    videoDays,
    gps,
    maxLoad,
    active,
    onSaved,
    onClose,
  ]);

  if (!open || !vehicle) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
      <div className="td-surface max-h-[90vh] w-full max-w-lg overflow-y-auto p-6">
        <h2 className="font-display text-lg font-semibold text-dark">Edit vehicle</h2>
        <p className="mt-1 font-mono text-xs text-gray">{vehicle.licensePlate}</p>
        <div className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
          <label className="block sm:col-span-2">
            <span className="text-xs text-gray">Make</span>
            <input
              className="input-field mt-1"
              value={make}
              onChange={(e) => setMake(e.target.value)}
            />
          </label>
          <label className="block">
            <span className="text-xs text-gray">Model</span>
            <input
              className="input-field mt-1"
              value={model}
              onChange={(e) => setModel(e.target.value)}
            />
          </label>
          <label className="block">
            <span className="text-xs text-gray">Year</span>
            <input
              type="number"
              className="input-field mt-1"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              min={1990}
              max={2100}
            />
          </label>
          <label className="block">
            <span className="text-xs text-gray">Color</span>
            <input
              className="input-field mt-1"
              value={color}
              onChange={(e) => setColor(e.target.value)}
            />
          </label>
          <label className="block">
            <span className="text-xs text-gray">License plate</span>
            <input
              className="input-field mt-1 font-mono"
              value={plate}
              onChange={(e) => setPlate(e.target.value)}
            />
          </label>
          <label className="block sm:col-span-2">
            <span className="text-xs text-gray">VIN (optional)</span>
            <input
              className="input-field mt-1 font-mono"
              value={vin}
              onChange={(e) => setVin(e.target.value)}
            />
          </label>
          <label className="block">
            <span className="text-xs text-gray">Registration #</span>
            <input
              className="input-field mt-1"
              value={regNum}
              onChange={(e) => setRegNum(e.target.value)}
            />
          </label>
          <label className="block">
            <span className="text-xs text-gray">Registration expires</span>
            <input
              type="date"
              className="input-field mt-1 font-mono"
              value={regExp}
              onChange={(e) => setRegExp(e.target.value)}
            />
          </label>
          <label className="block sm:col-span-2">
            <span className="text-xs text-gray">Insurance policy #</span>
            <input
              className="input-field mt-1"
              value={insPol}
              onChange={(e) => setInsPol(e.target.value)}
            />
          </label>
          <label className="block sm:col-span-2">
            <span className="text-xs text-gray">Insurance expires</span>
            <input
              type="date"
              className="input-field mt-1 font-mono"
              value={insExp}
              onChange={(e) => setInsExp(e.target.value)}
            />
          </label>
          <label className="flex items-center gap-2 sm:col-span-2">
            <input type="checkbox" checked={alarm} onChange={(e) => setAlarm(e.target.checked)} />
            Security alarm
          </label>
          <label className="flex items-center gap-2 sm:col-span-2">
            <input type="checkbox" checked={locked} onChange={(e) => setLocked(e.target.checked)} />
            Locked opaque storage
          </label>
          <label className="flex items-center gap-2 sm:col-span-2">
            <input type="checkbox" checked={video} onChange={(e) => setVideo(e.target.checked)} />
            Video surveillance
          </label>
          <label className="block">
            <span className="text-xs text-gray">Video retention (days)</span>
            <input
              type="number"
              className="input-field mt-1"
              value={videoDays}
              onChange={(e) => setVideoDays(Number(e.target.value))}
              min={1}
              max={3650}
            />
          </label>
          <label className="block">
            <span className="text-xs text-gray">GPS device id</span>
            <input
              className="input-field mt-1 font-mono"
              value={gps}
              onChange={(e) => setGps(e.target.value)}
            />
          </label>
          <label className="block sm:col-span-2">
            <span className="text-xs text-gray">Max load value (cents)</span>
            <input
              type="number"
              className="input-field mt-1 font-mono"
              value={maxLoad}
              onChange={(e) => setMaxLoad(Number(e.target.value))}
              min={0}
            />
          </label>
          <label className="flex items-center gap-2 sm:col-span-2">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
            Active
          </label>
        </div>
        {err ? <p className="mt-3 text-sm text-red">{err}</p> : null}
        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            className="btn-secondary"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary disabled:opacity-50"
            onClick={() => void submit()}
            disabled={busy}
          >
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
